import { loadingTypes } from '@/constants/loadingTypes';

import { ApiBase } from './api-base';

/**
 * @template R
 * @typedef {import('axios').AxiosResponse<R>} AxiosResponse<R>
 */
/**
 * @typedef {import('@/models/employee.interface').Employee} Employee
 * @typedef {import('@/models/schedule-entry.interface').ScheduleEntry} ScheduleEntry
 * @typedef {import('@/models/employee-assignments.interface').EmployeeAssignments} EmployeeAssignments
 * @typedef {import('../types/employee.api.types').GetEmployeesParams} GetEmployeesParams
 * @typedef {import('../types/employee.api.types').GetScheduleParams} GetScheduleParams
 * @typedef {import('../types/employee.api.types').GetEmployeeAssignmentsParams} GetEmployeeAssignmentsParams
 * @typedef {import('../types/additional-request-options.interface').AdditionalRequestOptions} AdditionalRequestOptions
 */

export class EmployeeApi extends ApiBase {
  /**
   * @private
   * @readonly
   */
  EMPLOYEES_API_ROOT = 'employees';

  /**
   * Get single employee by id
   * @param {string} id employee id
   * @returns {Promise<AxiosResponse<Employee>>}
   */
  getEmployee = (id) => this.apiClient.sendRequest({
    method: 'GET',
    url: `${this.EMPLOYEES_API_ROOT}/${id}`,
  }, { loadingType: loadingTypes.syncEmployeeButton });

  /**
   * Get multiple employees by project id or office ids
   * @param {GetEmployeesParams} [params={}] project id or offices ids
   * @returns {Promise<AxiosResponse<Employee[]>>}
   */
  getEmployees = ({ projectId, officesIds } = {}) => this.apiClient.sendRequest({
    method: 'GET',
    url: `${this.EMPLOYEES_API_ROOT}/`,
    params: {
      project_id: projectId,
      offices_ids: officesIds,
    },
  }, { loadingType: loadingTypes.none });

  /**
   * Get employees with filters
   * @param {Object} filters
   * @param {string} [filters.name_search]
   * @param {string[]} [filters.office_ids]
   * @param {string[]} [filters.division_ids]
   * @param {string[]} [filters.system_role_ids]
   * @param {string[]} [filters.status_values]
   * @param {string} [filters.project_id]
   * @param {string[]} [filters.offices_ids]
   * @returns {Promise<AxiosResponse<Employee[]>>}
   */
  getEmployeesWithFilters = (filters = {}) => this.apiClient.sendRequest({
    method: 'GET',
    url: `${this.EMPLOYEES_API_ROOT}/`,
    params: {
      name_search: filters.name_search,
      office_ids: filters.office_ids,
      division_ids: filters.division_ids,
      system_role_ids: filters.system_role_ids,
      status_values: filters.status_values,
      project_id: filters.project_id,
      offices_ids: filters.offices_ids,
    },
  }, { loadingType: loadingTypes.none });

  /**
   * Get multiple employees for timesheet page filters
   * @param {string} projectId
   * @param {string} officeId
   * @returns {Promise<AxiosResponse<Employee[]>>}
   */
  getEmployeesForTimeUnits = (projectId, officeId) => this.apiClient.sendRequest({
    method: 'GET',
    url: `${this.EMPLOYEES_API_ROOT}/time-units`,
    params: {
      project_id: projectId,
      office_id: officeId,
    },
  });

  /**
   * Update employee information
   * @param {string} id employee id
   * @param {Employee} employee payload with new employee data
   * @returns {Promise<AxiosResponse<null>>}
   */
  updateEmployee = (id, employee) => this.apiClient.sendRequest({
    method: 'PATCH',
    url: `${this.EMPLOYEES_API_ROOT}/${id}`,
    data: employee,
  });

  /**
   * Get employee schedule
   * @param {GetScheduleParams} params
   * @returns {Promise<AxiosResponse<ScheduleEntry[]>>}
   */
  getSchedule = (params) => this.apiClient.sendRequest({
    method: 'GET',
    url: `${this.EMPLOYEES_API_ROOT}/schedule`,
    params,
  }, { loadingType: loadingTypes.none });

  /* CURRENTLY UNUSED */ getScheduleByAssignment = (params) => this.apiClient.sendRequest({
    method: 'GET',
    url: `${this.EMPLOYEES_API_ROOT}/schedule-by-assignment`,
    params,
  }, { loadingType: loadingTypes.reportSchedule });

  /**
   * Get employee assignments
   * @param {GetEmployeeAssignmentsParams} params
   * @param {AdditionalRequestOptions} options
   * @returns {Promise<AxiosResponse<EmployeeAssignments[]>>}
   */
  getEmployeeAssignments = (params, options) => this.apiClient.sendRequest({
    method: 'GET',
    url: `${this.EMPLOYEES_API_ROOT}/${params.employeeId}/projects/`,
  }, options);

  getEmployeeOvertimeFlagsHistory = (params, options) => this.apiClient.sendRequest({
    method: 'GET',
    url: `${this.EMPLOYEES_API_ROOT}/${params.employeeId}/overime_flags_history`,
  }, options);

  /**
   * Update employee division
   * @param {string} employeeId
   * @param {{ division_id: string }} data
   * @returns {Promise<AxiosResponse<void>>}
   */
  updateEmployeeDivision = (employeeId, data) => this.apiClient.sendRequest({
    method: 'PUT',
    url: `${this.EMPLOYEES_API_ROOT}/${employeeId}/division`,
    data,
  });
}
